import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DailyHaikuCard from "../components/DailyHaikuCard";

const HERO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_renga-rooms/artifacts/s4rv8awu_Main%20Photo.JPEG";
const BAMBOO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_renga-rooms/artifacts/xbrai5ls_Daily%20Haiku.webp";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="hanko text-primary text-lg">連歌</span>
              <span className="text-xs tracking-[0.25em] uppercase text-muted-foreground">
                A house of linked verse
              </span>
            </div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Where poets gather<br />
              to link a single<br />
              <span className="italic text-primary">breath of season.</span>
            </h1>
            <p className="mt-8 max-w-md text-base text-muted-foreground leading-relaxed">
              Haiku House is a quiet studio in the spirit of Matsuo Bashō. Enter a
              Renga Room and add your verse to a living chain, one poet answering
              another, until the poem is sealed.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link
                to="/rooms"
                data-testid="hero-enter-rooms"
                className="bg-primary text-primary-foreground px-8 py-3 text-sm tracking-wide hover:-translate-y-0.5 transition-transform"
              >
                Enter the Renga Rooms
              </Link>
              <Link
                to="/basho"
                data-testid="hero-basho"
                className="text-sm border-b border-foreground/30 pb-1 hover:border-primary hover:text-primary transition-colors"
              >
                Discover the life of Bashō
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="md:col-span-5 relative"
          >
            <div className="relative border border-border">
              <img
                src={HERO}
                alt="Haiku House — ink sketch of a Japanese temple and blossoming tree"
                className="w-full h-[420px] md:h-[540px] object-contain bg-muted/30"
              />
              <div className="absolute -bottom-5 -left-5 bg-background border border-border px-6 py-4">
                <p className="hanko text-primary text-3xl leading-none">芭蕉</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED HAIKU BAND */}
      <section className="border-y border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <img src={BAMBOO} alt="Daily Haiku — ink sketch of a bamboo grove" className="w-full h-72 object-contain bg-muted/30 border border-border" />
          </div>
          <div className="md:col-span-7 md:pl-10">
            <DailyHaikuCard variant="hero" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-16">
          The way of renga
        </h2>
        <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
          {[
            { n: "一", t: "Open with a hokku", d: "A host begins the chain with an opening 5-7-5 verse, naming the season and setting the mood." },
            { n: "二", t: "Link, don't repeat", d: "Each poet answers the verse before with a 7-7 or 5-7-5 link — shifting the scene while keeping the thread." },
            { n: "三", t: "Seal the poem", d: "When the chain feels whole, the host seals the renga. It rests in the house for all to read." },
          ].map((s) => (
            <div key={s.t} className="bg-background p-10 hover:bg-muted/40 transition-colors">
              <span className="hanko text-primary text-3xl">{s.n}</span>
              <h3 className="font-serif text-2xl mt-5 mb-3">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
